import { create } from 'zustand';
import apiClient from '../lib/api';

export interface SalesOpportunity {
    id: string;
    tenantId: string;
    title: string;
    customerId: string;
    amount: number;
    stage: string;
    probability: number;
    expectedCloseDate?: string;
    assignedTo?: string;
    createdAt: string;
    updatedAt: string;
}

interface SalesState {
    opportunities: SalesOpportunity[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchOpportunities: (filters?: any) => Promise<void>;
    createOpportunity: (data: Partial<SalesOpportunity>) => Promise<void>;
    updateOpportunity: (id: string, data: Partial<SalesOpportunity>) => Promise<void>;
}

export const useSalesStore = create<SalesState>((set, get) => ({
    opportunities: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchOpportunities: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/money/sales', { params: filters });
            const { data, pagination } = response.data;
            set({ opportunities: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch opportunities', isLoading: false });
        }
    },
    createOpportunity: async (data: Partial<SalesOpportunity>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/money/sales', data);
            const newOp = response.data;
            set((state) => ({
                opportunities: [newOp, ...state.opportunities],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create opportunity', isLoading: false });
            throw error;
        }
    },
    updateOpportunity: async (id: string, data: Partial<SalesOpportunity>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/money/sales/${id}`, data);
            const updatedOp = response.data;
            set((state) => ({
                opportunities: state.opportunities.map((o) => (o.id === id ? updatedOp : o)),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update opportunity', isLoading: false });
            throw error;
        }
    },
}));
